import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function SwipeToExecute({ orderType = 'BUY', onExecute, disabled = false }) {
  const { colors } = useTheme();
  const [swipePosition, setSwipePosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const isBuy = orderType === 'BUY';
  const bgColor = isBuy ? '#6366F1' : '#EF4444';
  const text = isBuy ? 'SWIPE TO BUY' : 'SWIPE TO SELL';

  const handleStart = (e) => {
    if (disabled) return;
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMove = (clientX) => {
    if (!isDragging || disabled) return;

    const container = containerRef.current;
    const button = buttonRef.current;
    if (!container || !button) return;

    const containerRect = container.getBoundingClientRect();
    const buttonWidth = button.offsetWidth;
    const maxSwipe = containerRect.width - buttonWidth - 8;

    let newPosition = clientX - containerRect.left - buttonWidth / 2;
    newPosition = Math.max(0, Math.min(newPosition, maxSwipe));

    setSwipePosition(newPosition);

    // Execute if swiped more than 80%
    if (newPosition > maxSwipe * 0.8) {
      setIsDragging(false);
      setSwipePosition(maxSwipe);
      setTimeout(() => {
        onExecute();
        setSwipePosition(0);
      }, 200);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    // Reset if not completed
    setSwipePosition(0);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative h-14 rounded-full overflow-hidden"
      style={{
        backgroundColor: bgColor,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      data-testid="swipe-to-execute"
    >
      {/* Background text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-white font-semibold text-sm tracking-wide">{text}</span>
      </div>

      {/* Swipe button */}
      <div
        ref={buttonRef}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        className="absolute top-1 left-1 h-12 w-12 bg-white rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg"
        style={{
          transform: `translateX(${swipePosition}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        <ChevronRight className="w-6 h-6" style={{ color: bgColor }} />
      </div>
    </div>
  );
}

export default SwipeToExecute;
